// Single source of truth for marketplace constants. Mirrors the build spec.

export const PLATFORM_COMMISSION_PCT = 10;

// Master switch for the live payment flow (Paystack checkout + escrow).
// While off: bookings are created without a checkout step, no one is
// redirected to Paystack, and payment is shown as "not live yet" rather than
// pending/unpaid. Requires PAYSTACK_SECRET_KEY (Secret Manager in prod,
// functions/.secret.local on the emulator).
export const PAYMENTS_ENABLED = true;

export const CANCELLATION_WINDOW_HOURS = 24;
export const LATE_CANCELLATION_PENALTY_PCT = 10;
export const AUTO_CONFIRM_HOURS = 48;
export const SELLER_RESPONSE_HOURS = 24;

// Card pre-authorization holds last at most 30 days (Paystack). A booking is
// capped this many days ahead so one hold always covers the service plus the
// post-service confirmation/auto-capture window (service date + ~48h + buffer).
export const MAX_BOOKING_LEAD_DAYS = 25;
export const PREAUTH_MAX_DAYS = 30;
export const NO_SHOW_GRACE_MINUTES = 30;

// Open requests (reverse marketplace)
export const REQUEST_EXPIRY_DAYS = 5;
export const REQUEST_MAX_OPEN = 5;
export const REQUEST_MAX_EXTENSIONS = 2;
export const REQUEST_TITLE_MAX_CHARS = 80;
export const REQUEST_DESCRIPTION_MAX_CHARS = 600;
export const OFFER_MESSAGE_MAX_CHARS = 400;

export const SERVICE_VIDEO_MAX_SECONDS = 30;
export const PROFILE_GALLERY_MAX_VIDEOS = 6;
export const REVIEW_VIDEO_MAX_SECONDS = 60;
export const SERVICE_DESCRIPTION_MAX_CHARS = 1000;
export const REVIEW_TEXT_MAX_CHARS = 500;
export const BIO_MAX_CHARS = 280;

export const LATE_NIGHT_START_HOUR = 22;
export const LATE_NIGHT_END_HOUR = 6;

export const SERVICE_CATEGORIES = [
  'hair',
  'brows_lashes',
  'nails',
  'barbering',
  'makeup',
] as const;
export type ServiceCategory = (typeof SERVICE_CATEGORIES)[number];

export const CATEGORY_LABELS: Record<ServiceCategory, string> = {
  hair: 'Hair',
  brows_lashes: 'Brows & Lashes',
  nails: 'Nails',
  barbering: 'Barbering',
  makeup: 'Makeup',
};

// ---------- Two-stage review: durability follow-up ----------
// Stage 1 (at completion) captures the experience. Stage 2, sent this many days
// later, captures how long the result actually lasted and writes it onto the
// same review. Nudges are snoozeable and capped. SA has no DST → SAST = UTC+2.
export const LONGEVITY_PROMPT_DELAY_DAYS = 21;
export const LONGEVITY_SNOOZE_HOURS = 3;
export const LONGEVITY_MAX_REMINDERS = 3;
export const LONGEVITY_WINDOW_START_HOUR = 8; // SAST — no nudges before this
export const LONGEVITY_WINDOW_END_HOUR = 20; // SAST — no nudges at/after this

// Categories where "how long did it last" is a real buying signal. Must match
// the WEAR_LIFE keys in apps/web/src/lib/wearLife.ts.
export const LONGEVITY_CATEGORIES: readonly ServiceCategory[] = ['hair', 'nails', 'brows_lashes'];
export const categoryHasWearLife = (category: ServiceCategory): boolean =>
  LONGEVITY_CATEGORIES.includes(category);

// Campus labels are free-form and sub-campus specific ("Wits East", "Wits Braam",
// "UJ APK"), so exact-string matching wrongly hides same-university requests from
// sellers. Match at the institution level instead, and never exclude when either
// side has no campus set. (Proper proximity by geolocation is a later refinement.)
const CAMPUS_GENERIC_WORDS = new Set(['university', 'the', 'of', 'college', 'campus']);

// Canonical institution keys for SA universities, so the same institution written
// differently — "Wits" vs "University of the Witwatersrand", "UJ" vs "University
// of Johannesburg", "UP"/"Tuks" vs "University of Pretoria" — resolves to one key.
// A single-word alias matches a whole token; a multi-word alias matches as a
// substring of the normalised campus string.
const CAMPUS_ALIAS_GROUPS: ReadonlyArray<readonly [string, readonly string[]]> = [
  ['wits', ['wits', 'witwatersrand']],
  ['uj', ['uj', 'johannesburg']],
  ['uct', ['uct', 'cape town']],
  ['up', ['up', 'tuks', 'tukkies', 'pretoria']],
  ['stellenbosch', ['stellenbosch', 'stellies', 'maties']],
  ['ukzn', ['ukzn', 'kwazulu', 'kwazulu natal']],
  ['uwc', ['uwc', 'western cape']],
  ['ufs', ['ufs', 'kovsies', 'free state', 'bloemfontein']],
  ['nwu', ['nwu', 'north west', 'potchefstroom', 'pukke']],
  ['rhodes', ['rhodes']],
  ['nmu', ['nmu', 'nmmu', 'nelson mandela']],
  ['unisa', ['unisa']],
  ['tut', ['tut', 'tshwane']],
  ['cput', ['cput', 'cape peninsula']],
  ['dut', ['dut']],
  ['ufh', ['ufh', 'fort hare']],
  ['ul', ['ul', 'limpopo', 'turfloop']],
  ['univen', ['univen', 'venda']],
  ['wsu', ['wsu', 'walter sisulu']],
  ['spu', ['spu', 'sol plaatje']],
  ['ump', ['ump', 'mpumalanga']],
  ['smu', ['smu', 'sefako', 'makgatho']],
  ['unizulu', ['unizulu', 'zululand']],
  ['vut', ['vut', 'vaal']],
  ['mut', ['mut', 'mangosuthu']],
  ['cut', ['cut']],
];

function normaliseCampus(s: string): string {
  return s.trim().toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
}

function campusInstitutionKey(campus: string): string {
  const norm = normaliseCampus(campus);
  const tokens = new Set(norm.split(' ').filter(Boolean));
  for (const [key, aliases] of CAMPUS_ALIAS_GROUPS) {
    for (const alias of aliases) {
      const a = normaliseCampus(alias);
      if (a.includes(' ') ? norm.includes(a) : tokens.has(a)) return key;
    }
  }
  // Unknown institution: fall back to the first meaningful word.
  const words = norm.split(' ').filter((w) => w && !CAMPUS_GENERIC_WORDS.has(w));
  return words[0] ?? norm;
}
export function sameCampusRegion(a?: string | null, b?: string | null): boolean {
  if (!a || !b) return true; // missing campus on either side → don't exclude
  const na = a.trim().toLowerCase();
  const nb = b.trim().toLowerCase();
  if (na === nb) return true;
  return campusInstitutionKey(a) === campusInstitutionKey(b);
}

// Legacy default tier ids. Tiers are seller-defined now (any id + custom
// label/price); these remain only as the starting template for new listings
// and for services created before custom tiers shipped.
export const PRICING_TIERS = ['basic', 'standard', 'premium'] as const;
export type PricingTier = (typeof PRICING_TIERS)[number];

export const OPERATES_FROM_OPTIONS = [
  'seller_location',
  'buyer_location',
  'shared_spot',
] as const;
export type OperatesFrom = (typeof OPERATES_FROM_OPTIONS)[number];

export const BOOKING_STATUSES = [
  'requested',
  'counter_proposed',
  'accepted',
  'declined',
  'cancelled_by_buyer',
  'cancelled_by_seller',
  'no_show_buyer',
  'no_show_seller',
  'in_progress',
  'awaiting_confirmation',
  'completed',
  'disputed',
] as const;
export type BookingStatus = (typeof BOOKING_STATUSES)[number];

export const PAYMENT_STATUSES = [
  'unpaid',
  'pending',
  'authorized', // pre-auth hold placed on the buyer's card; funds NOT captured
  'paid', // captured (pulled + split to seller subaccount / platform)
  'released', // pre-auth hold dropped without capture — the "refund"
  'refunded',
  'partially_refunded',
  'failed',
] as const;
export type PaymentStatus = (typeof PAYMENT_STATUSES)[number];

export const VERIFICATION_STATUSES = [
  'unverified',
  'pending',
  'needs_info',
  'verified',
  'rejected',
] as const;
export type VerificationStatus = (typeof VERIFICATION_STATUSES)[number];

export const VERIFICATION_REJECTION_CODES = [
  'student_number_not_found',
  'institution_not_detected',
  'institution_mismatch',
  'unclear_photo',
  'name_mismatch',
  'processing_error',
  'admin_rejected',
  'duplicate_student',
] as const;
export type VerificationRejectionCode = (typeof VERIFICATION_REJECTION_CODES)[number];

/** User-facing copy for a failed card verification. */
export function verificationRejectionMessage(
  code: VerificationRejectionCode | undefined,
  custom?: string | null,
): string {
  if (custom?.trim()) return custom.trim();
  switch (code) {
    case 'student_number_not_found':
      return "We couldn't find your student number on the card. Make sure the number line at the bottom is in focus and not cut off.";
    case 'institution_not_detected':
      return "We read your card but couldn't confirm the university. Check you picked the right institution and the card logo is visible.";
    case 'institution_mismatch':
      return "The university on your card doesn't match the institution you selected. Pick the one on your card and upload again.";
    case 'unclear_photo':
      return 'The photo was too blurry or dark for us to verify automatically. Try a flat, glare-free shot in good light.';
    case 'name_mismatch':
      return "The name on your card doesn't match the full name on your account. Use the same name and surname as on your student card, then upload again.";
    case 'processing_error':
      return 'Something went wrong on our side while reading your card. Please try uploading again in a minute.';
    case 'admin_rejected':
      return "We couldn't verify your student status. Contact support if you think this is a mistake.";
    case 'duplicate_student':
      return 'This student number is already linked to another Makhe account. Contact support if you need help.';
    default:
      return "We couldn't verify your last photo. Upload a clearer shot — check the tips below.";
  }
}

// Curated student email-domain whitelist for v1 auto-verification.
// Sourced from SAFIRE IdP metadata, official university IT pages, and IIE/private
// college student portals. Append as more institutions onboard.
export const STUDENT_EMAIL_DOMAINS: ReadonlyArray<{
  domain: string;
  institution: string;
}> = [
  // Private colleges & IIE brands
  { domain: 'myboston.co.za', institution: 'Boston City Campus' },
  { domain: 'myemeris.edu.za', institution: 'Emeris (IIE — Varsity College, MSA, Vega)' },
  { domain: 'rcconnect.ac.za', institution: 'IIE Rosebank College' },
  { domain: 'my.milpark.ac.za', institution: 'Milpark Education' },
  { domain: 'students.mancosa.co.za', institution: 'MANCOSA' },
  { domain: 'mancosaconnect.ac.za', institution: 'MANCOSA' },
  { domain: 'gwc.ac.za', institution: 'George Whitefield College' },
  { domain: 'sats.ac.za', institution: 'South African Theological Seminary' },
  { domain: 'sats.edu.za', institution: 'South African Theological Seminary' },
  // TVET
  { domain: 'fbstudent.org.za', institution: 'False Bay TVET College' },
  // Universities of technology
  { domain: 'cput.ac.za', institution: 'Cape Peninsula University of Technology' },
  { domain: 'mycput.ac.za', institution: 'Cape Peninsula University of Technology' },
  { domain: 'cut.ac.za', institution: 'Central University of Technology' },
  { domain: 'stud.cut.ac.za', institution: 'Central University of Technology' },
  { domain: 'dut.ac.za', institution: 'Durban University of Technology' },
  { domain: 'dut4life.ac.za', institution: 'Durban University of Technology' },
  { domain: 'mut.ac.za', institution: 'Mangosuthu University of Technology' },
  { domain: 'tut.ac.za', institution: 'Tshwane University of Technology' },
  { domain: 'tut4life.ac.za', institution: 'Tshwane University of Technology' },
  { domain: 'vut.ac.za', institution: 'Vaal University of Technology' },
  { domain: 'edu.vut.ac.za', institution: 'Vaal University of Technology' },
  // Traditional universities
  { domain: 'mandela.ac.za', institution: 'Nelson Mandela University' },
  { domain: 'nmmu.ac.za', institution: 'Nelson Mandela University' },
  { domain: 'live.nmmu.ac.za', institution: 'Nelson Mandela University' },
  { domain: 'nwu.ac.za', institution: 'North-West University' },
  { domain: 'mynwu.ac.za', institution: 'North-West University' },
  { domain: 'mysis-nwu.ac.za', institution: 'North-West University' },
  { domain: 'campus.ru.ac.za', institution: 'Rhodes University' },
  { domain: 'graduate.students.ru.ac.za', institution: 'Rhodes University' },
  { domain: 'postgrad.students.ru.ac.za', institution: 'Rhodes University' },
  { domain: 'rhodes.ac.za', institution: 'Rhodes University' },
  { domain: 'ru.ac.za', institution: 'Rhodes University' },
  { domain: 'undergrad.students.ru.ac.za', institution: 'Rhodes University' },
  { domain: 'wf.ru.ac.za', institution: 'Rhodes University' },
  { domain: 'spu.ac.za', institution: 'Sol Plaatje University' },
  { domain: 'sun.ac.za', institution: 'Stellenbosch University' },
  { domain: 'myuct.ac.za', institution: 'University of Cape Town' },
  { domain: 'uct.ac.za', institution: 'University of Cape Town' },
  { domain: 'ufh.ac.za', institution: 'University of Fort Hare' },
  { domain: 'ufs.ac.za', institution: 'University of the Free State' },
  { domain: 'ufs4life.ac.za', institution: 'University of the Free State' },
  { domain: 'student.uj.ac.za', institution: 'University of Johannesburg' },
  { domain: 'uj.ac.za', institution: 'University of Johannesburg' },
  { domain: 'ukzn.ac.za', institution: 'University of KwaZulu-Natal' },
  { domain: 'stu.ukzn.ac.za', institution: 'University of KwaZulu-Natal' },
  { domain: 'keyaka.ul.ac.za', institution: 'University of Limpopo' },
  { domain: 'myturf.ul.ac.za', institution: 'University of Limpopo' },
  { domain: 'ul.ac.za', institution: 'University of Limpopo' },
  { domain: 'ump.ac.za', institution: 'University of Mpumalanga' },
  { domain: 'tuks.co.za', institution: 'University of Pretoria' },
  { domain: 'up.ac.za', institution: 'University of Pretoria' },
  { domain: 'mylife.unisa.ac.za', institution: 'University of South Africa' },
  { domain: 'unisa.ac.za', institution: 'University of South Africa' },
  { domain: 'myuwc.ac.za', institution: 'University of the Western Cape' },
  { domain: 'uwc.ac.za', institution: 'University of the Western Cape' },
  { domain: 'students.wits.ac.za', institution: 'University of the Witwatersrand' },
  { domain: 'wits.ac.za', institution: 'University of the Witwatersrand' },
  { domain: 'mvula.univen.ac.za', institution: 'University of Venda' },
  { domain: 'univen.ac.za', institution: 'University of Venda' },
  { domain: 'stu.unizulu.ac.za', institution: 'University of Zululand' },
  { domain: 'unizulu.ac.za', institution: 'University of Zululand' },
  { domain: 'mywsu.ac.za', institution: 'Walter Sisulu University' },
  { domain: 'wsu.ac.za', institution: 'Walter Sisulu University' },
];

/** Parent domains where any subdomain is treated as a student address. */
export const STUDENT_EMAIL_DOMAIN_SUFFIXES: ReadonlyArray<{
  suffix: string;
  institution: string;
}> = [{ suffix: 'uct.ac.za', institution: 'University of Cape Town' }];

export function matchStudentEmailDomain(
  email: string,
): { domain: string; institution: string } | null {
  const domain = email.split('@')[1]?.toLowerCase();
  if (!domain) return null;
  const exact = STUDENT_EMAIL_DOMAINS.find((d) => d.domain === domain);
  if (exact) return exact;
  const suffix = STUDENT_EMAIL_DOMAIN_SUFFIXES.find(
    (s) => domain === s.suffix || domain.endsWith('.' + s.suffix),
  );
  if (suffix) return { domain, institution: suffix.institution };
  return null;
}

/** Unique institution names for personal-email card verification picker. */
export function listStudentInstitutions(): readonly string[] {
  const names = new Set<string>();
  for (const { institution } of STUDENT_EMAIL_DOMAINS) names.add(institution);
  for (const { institution } of STUDENT_EMAIL_DOMAIN_SUFFIXES) names.add(institution);
  return [...names].sort((a, b) => a.localeCompare(b));
}

export function isKnownStudentInstitution(name: string): boolean {
  return listStudentInstitutions().includes(name);
}

/** True when a string looks like a student number, not a human name. */
export function isLikelyStudentNumberLocalPart(local: string): boolean {
  const s = local.trim();
  if (!s) return false;
  if (/^\d{5,12}$/.test(s)) return true;
  if (/^st\d{6,10}$/i.test(s)) return true;
  return false;
}

/** Pick a public display name — never fall back to a numeric student number. */
export function resolveInitialDisplayName(
  authDisplayName: string | null | undefined,
  email: string,
): string {
  const fromAuth = authDisplayName?.trim();
  if (fromAuth && !isLikelyStudentNumberLocalPart(fromAuth)) return fromAuth;

  const local = email.split('@')[0]?.trim() ?? '';
  if (local && !isLikelyStudentNumberLocalPart(local) && /[a-zA-Z]/.test(local)) {
    return local
      .replace(/[._-]+/g, ' ')
      .replace(/\s+/g, ' ')
      .trim()
      .replace(/\b\w/g, (c) => c.toUpperCase());
  }
  return 'Student';
}

/** Prefer the name the user typed at sign-up, then Firestore, then email-derived fallback. */
export function pickUserDisplayName(
  authDisplayName: string | null | undefined,
  existingDisplayName: string | null | undefined,
  email: string,
): string {
  const fromAuth = authDisplayName?.trim();
  if (fromAuth && !isLikelyStudentNumberLocalPart(fromAuth)) return fromAuth;

  const fromExisting = existingDisplayName?.trim();
  if (fromExisting && !isLikelyStudentNumberLocalPart(fromExisting)) return fromExisting;

  return resolveInitialDisplayName(authDisplayName, email);
}

export const REPORT_REASONS = [
  'harassment',
  'no_show',
  'fraud',
  'inappropriate_content',
  'fake_listing',
  'safety',
  'other',
] as const;
export type ReportReason = (typeof REPORT_REASONS)[number];

// Money helpers — money is always stored as integer ZAR cents.
export const zarCents = (rands: number): number => Math.round(rands * 100);
export const zarFromCents = (cents: number): number => cents / 100;
export const formatZAR = (cents: number): string =>
  `R${(cents / 100).toFixed(2)}`;
